import { type MemoryBankNoteMetadata } from '@erp/rpc';
import { Badge } from '../ui/badge';
import {
  WORKFLOW_STEP_LABELS,
  WORKFLOW_STEP_COLORS,
} from '@/hooks/useWorkflowStepFilter';
import { WORKFLOW_COLORS, WORKFLOW_LABELS } from '@/hooks/useWorkflowFilter';
import { cn } from '@/lib/utils';
import { AIWorkflow, type WorkflowStep } from '@erp/shared-types';
import { XIcon } from '@phosphor-icons/react';

type InternalMetadataName = 'workflow' | 'workflowStep';
const INTERNAL_METADATA_NAMES: InternalMetadataName[] = [
  'workflow',
  'workflowStep',
];

function useIsSystemMetadata(name: string): name is InternalMetadataName {
  return INTERNAL_METADATA_NAMES.includes(name as InternalMetadataName);
}

function MemoryBankNoteSystemMetadatum({
  name,
  value,
  onRemove,
}: {
  name: InternalMetadataName;
  value: MemoryBankNoteMetadata[keyof MemoryBankNoteMetadata];
  onRemove?: () => void;
}) {
  const content = (
    <>
      {name === 'workflow' && WORKFLOW_LABELS[value as AIWorkflow]}
      {name === 'workflowStep' && WORKFLOW_STEP_LABELS[value as WorkflowStep]}
      {onRemove && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          className="ml-1 hover:bg-black/10 rounded-sm transition-colors"
          aria-label="Remove"
        >
          <XIcon className="h-3 w-3" />
        </button>
      )}
    </>
  );

  switch (name) {
    case 'workflow':
      return (
        <Badge
          variant="secondary"
          className={cn(
            WORKFLOW_COLORS[value as AIWorkflow],
            onRemove && 'pr-1',
          )}
        >
          {content}
        </Badge>
      );
    case 'workflowStep':
      return (
        <Badge
          variant="secondary"
          className={cn(
            WORKFLOW_STEP_COLORS[value as WorkflowStep],
            onRemove && 'pr-1',
          )}
        >
          {content}
        </Badge>
      );
  }
}

export function MemoryBankNoteMetadatum({
  name,
  value,
  onRemove,
}: {
  name: string;
  value: MemoryBankNoteMetadata[keyof MemoryBankNoteMetadata];
  onRemove?: () => void;
}) {
  const isInternalMetadata = useIsSystemMetadata(name);

  if (isInternalMetadata) {
    return (
      <MemoryBankNoteSystemMetadatum
        name={name}
        value={value}
        onRemove={onRemove}
      />
    );
  }

  return (
    <Badge variant="outline" className={cn('text-xs', onRemove && 'pr-1')}>
      {name}:{' '}
      <span className="font-normal text-secondary-foreground ps-1">
        {String(value)}
      </span>
      {onRemove && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          className="ml-1 hover:bg-black/10 rounded-sm transition-colors"
          aria-label="Remove"
        >
          <XIcon className="h-3 w-3" />
        </button>
      )}
    </Badge>
  );
}
